import { computed, ref } from "vue";
import { pdfjs } from "@/utils/pdfjs";
import { orderInfoService } from "@/services/orderInfoService";
const GROUP_ORDER = ["response", "chart", "necessity", "form"];
const IMAGE_POINTS_PER_PIXEL = 0.75;
function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
const STOP_WORDS = new Set([
    "the",
    "and",
    "for",
    "with",
    "that",
    "this",
    "from",
    "was",
    "are",
    "has",
    "have",
    "not",
    "patient",
]);
function queryWords(query) {
    return query.replace(/^"|"$/g, "").trim().split(/\s+/).filter(Boolean);
}
function phrasePattern(words) {
    return new RegExp(words.map(escapeRegExp).join("\\s+"), "gi");
}
function anyWordPattern(words) {
    return new RegExp(`\\b(?:${words.map(escapeRegExp).join("|")})`, "gi");
}
function loadImage(url) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("image failed to load"));
        image.src = url;
    });
}
/** Loads every document attached to a PA, opens them with pdf.js, and searches their text. */
export function useDocumentSet() {
    const documents = ref([]);
    const status = ref("idle");
    const error = ref(null);
    const proxies = new Map();
    const textCache = new Map();
    let generation = 0;
    const ordered = computed(() => [...documents.value].sort((a, b) => {
        const byGroup = GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group);
        if (byGroup)
            return byGroup;
        return (b.receivedAt ?? "").localeCompare(a.receivedAt ?? "");
    }));
    const pageCount = computed(() => ordered.value.reduce((sum, doc) => sum + Math.max(doc.pages.length, 1), 0));
    function dispose() {
        proxies.forEach((proxy) => proxy.destroy());
        proxies.clear();
        textCache.clear();
        documents.value = [];
    }
    function patch(key, changes) {
        const index = documents.value.findIndex((doc) => doc.key === key);
        if (index >= 0)
            documents.value[index] = { ...documents.value[index], ...changes };
    }
    async function openPdf(doc, token) {
        const proxy = await pdfjs.getDocument({ url: doc.url, isEvalSupported: false }).promise;
        if (token !== generation) {
            proxy.destroy();
            return;
        }
        proxies.set(doc.key, proxy);
        const pages = [];
        for (let number = 1; number <= proxy.numPages; number += 1) {
            const viewport = (await proxy.getPage(number)).getViewport({ scale: 1 });
            pages.push({ width: viewport.width, height: viewport.height });
        }
        if (token !== generation)
            return;
        patch(doc.key, { status: "ready", pages });
        void checkText(doc.key, pages.length, token);
    }
    async function checkText(key, pageCount, token) {
        try {
            for (let number = 1; number <= pageCount; number += 1) {
                const { text } = await pageText(key, number);
                if (token !== generation)
                    return;
                if (text.trim()) {
                    patch(key, { hasText: true });
                    return;
                }
            }
            patch(key, { hasText: false });
        }
        catch {
            /* text extraction is best effort; the document still renders */
        }
    }
    async function openImage(doc, token) {
        const image = await loadImage(doc.url);
        if (token !== generation)
            return;
        const size = {
            width: image.naturalWidth * IMAGE_POINTS_PER_PIXEL,
            height: image.naturalHeight * IMAGE_POINTS_PER_PIXEL,
        };
        patch(doc.key, { status: "ready", pages: [size], hasText: false });
    }
    async function load(orderKey) {
        const token = ++generation;
        dispose();
        status.value = "loading";
        error.value = null;
        try {
            const response = await orderInfoService.getOrderDocuments(orderKey);
            if (token !== generation)
                return;
            documents.value = response.documents.map((doc) => ({
                ...doc,
                status: "loading",
                pages: [],
                hasText: null,
            }));
            status.value = "ready";
            await Promise.all(documents.value.map((doc) => {
                if (doc.kind === "file") {
                    patch(doc.key, { status: "error" });
                    return Promise.resolve();
                }
                return (doc.kind === "image" ? openImage(doc, token) : openPdf(doc, token)).catch(() => {
                    if (token === generation)
                        patch(doc.key, { status: "error" });
                });
            }));
        }
        catch {
            if (token !== generation)
                return;
            status.value = "error";
            error.value = "Documents could not be loaded.";
        }
    }
    function proxy(key) {
        return proxies.get(key);
    }
    function pageText(key, pageNumber) {
        const cacheKey = `${key}:${pageNumber}`;
        const cached = textCache.get(cacheKey);
        if (cached)
            return cached;
        const doc = proxies.get(key);
        if (!doc)
            return Promise.resolve({ text: "", starts: [], lengths: [] });
        const pending = (async () => {
            const content = await (await doc.getPage(pageNumber)).getTextContent();
            const starts = [];
            const lengths = [];
            let text = "";
            for (const item of content.items) {
                if (!("str" in item))
                    continue;
                starts.push(text.length);
                lengths.push(item.str.length);
                text += `${item.str} `;
            }
            return { text, starts, lengths };
        })();
        textCache.set(cacheKey, pending);
        return pending;
    }
    async function search(query) {
        const words = queryWords(query);
        if (!words.length)
            return { hits: [], mode: "phrase" };
        const exact = await scan(phrasePattern(words));
        const quoted = /^".+"$/.test(query.trim());
        const significant = words.filter((word) => word.length >= 3 && !STOP_WORDS.has(word.toLowerCase()));
        if (exact.length || quoted || words.length < 2 || !significant.length)
            return { hits: exact, mode: "phrase" };
        return { hits: await scan(anyWordPattern(significant)), mode: "words" };
    }
    async function scan(pattern) {
        const hits = [];
        for (const doc of ordered.value) {
            if (doc.kind !== "pdf" || doc.status !== "ready")
                continue;
            for (let number = 1; number <= doc.pages.length; number += 1) {
                const { text } = await pageText(doc.key, number);
                pattern.lastIndex = 0;
                for (let match = pattern.exec(text); match; match = pattern.exec(text)) {
                    const start = match.index;
                    const end = start + match[0].length;
                    const before = text
                        .slice(Math.max(0, start - 40), start)
                        .replace(/^\S*\s/, "")
                        .replace(/\s+/g, " ");
                    const after = text
                        .slice(end, end + 56)
                        .replace(/\s\S*$/, "")
                        .replace(/\s+/g, " ");
                    hits.push({
                        docKey: doc.key,
                        pageNumber: number,
                        start,
                        end,
                        snippet: `${before}\u0000${match[0]}\u0000${after}`,
                    });
                    if (match[0].length === 0)
                        pattern.lastIndex += 1;
                }
            }
        }
        return hits;
    }
    return { documents: ordered, status, error, pageCount, load, dispose, proxy, pageText, search };
}
